import { makeAutoObservable } from 'mobx'
import { EnumAlertBannerVariant, type IAlertBannerProps } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { SettingsPath } from 'pages/settings/route/settingsPath'
import { CallingRoutesSteps } from 'pages/settings/pages/compliance/pages/calling/route/type'

export class CallingBannerStore {
  private _isHidden = false

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  init = async () => {
    if (!this.voiceIntegrity) {
      await this._entities.complianceStore.complianceVoiceServicesStore.fetchComplianceVoiceServicesList()
    }
  }

  hideBanner = () => {
    this._isHidden = true
  }

  get voiceIntegrity() {
    return this._entities.complianceStore.organizationVoiceIntegrity
  }

  get isVoiceIntegrityRejected() {
    return this.voiceIntegrity?.status === 'failed'
  }

  get cnamRejected() {
    return this._entities.complianceStore.organizationCnam?.isRejected
  }

  get isAdminOrOwner() {
    return (
      this._entities.usersStore.user?.isAdminRole || this._entities.usersStore.user?.isOwnerRole
    )
  }

  get isNeedAdminAsk() {
    const { isViewOnlyRole, isMemberRole, isManagerRole } = this._entities.usersStore.user || {}

    return isViewOnlyRole || isMemberRole || isManagerRole
  }

  get config(): Omit<IAlertBannerProps, 'ariaLabel'> | null {
    if (this._isHidden) return null

    if (this.isVoiceIntegrityRejected) {
      const useCaseName = this.voiceIntegrity?.use_case || ''
      const title = this.isNeedAdminAsk
        ? `Uh oh! Your Voice Integrity application was rejected. Ask one of your admins to update and resubmit to remediate ${useCaseName} labels on your calls.`
        : `Uh oh! Your Voice Integrity application was rejected. Update and resubmit to remediate ${useCaseName} labels on your calls.`

      return {
        title,
        variant: EnumAlertBannerVariant.Error,
        actionPrimary: {
          text: 'Update',
          onAction: () => {
            this.hideBanner()

            uiStore.changeRoute({
              path: `/${SettingsPath.compliance.calling.root}/${CallingRoutesSteps.enableVoiceIntegrity}`,
            })
          },
        },
        actionClose: {
          closeId: `voice_integrity_rejected_${this._entities.usersStore.user_owner_id}`,
        },
      }
    }

    if (this.cnamRejected) {
      const title = this.isNeedAdminAsk
        ? 'Uh oh! Your CNAM Caller ID application was rejected. Ask one of your admins to update and resubmit to brand your outgoing calls.'
        : 'Uh oh! Your CNAM Caller ID application was rejected. Update and resubmit to brand your outgoing calls.'
      return {
        title,
        variant: EnumAlertBannerVariant.Error,
        actionPrimary: {
          text: 'See details',
          onAction: () => {
            uiStore.changeRoute({
              path: `/${SettingsPath.compliance.calling.root}`,
            })
          },
        },
        actionClose: {
          closeId: `cnam_rejected_${this._entities.usersStore.user_owner_id}`,
        },
      }
    }

    return null
  }
}
